import { App, View } from '@gin-betting/core';
import { last } from 'lodash';

import { CONFIG_BY_SPORTS, metaTypesNumerable } from 'scoreMetaConfig';
import { MatchPartInfo } from 'types';
import getByKey from './getByKey';

const MetaTypesTitleMapping = {
    [App.MetaTypes.TYPE_SET]: 'S',
    [App.MetaTypes.TYPE_GOLDEN_SET]: 'GS',
    [App.MetaTypes.TYPE_PERIOD_1ST_HALF]: '1H',
    [App.MetaTypes.TYPE_PERIOD_1ST_EXTRA]: '1EH',
    [App.MetaTypes.TYPE_PERIOD_2ND_HALF]: '2H',
    [App.MetaTypes.TYPE_PERIOD_2ND_EXTRA]: '2EH',
    [App.MetaTypes.TYPE_PERIOD_PENALTIES]: 'Aft.Penalty',
    [App.MetaTypes.TYPE_QUARTER]: 'Q',
    [App.MetaTypes.TYPE_PERIOD]: 'P',
    [App.MetaTypes.TYPE_REGULAR]: 'P',
    [App.MetaTypes.TYPE_OVERTIME]: 'Overtime',
    [App.MetaTypes.TYPE_BULLETS]: 'Bullets',
    [App.MetaTypes.TYPE_MAP]: 'Map',
    [App.MetaTypes.TYPE_INNING]: 'IN',
};

const checkIsNumerableMetaTypes = (
    metaType: App.MetaTypes,
    sportId: string
): boolean => {
    const config = CONFIG_BY_SPORTS[sportId];

    const customNumerable =
        config?.customMetaTypesNumerable ?? metaTypesNumerable;

    return customNumerable.includes(metaType);
};

interface TextForMatchPart {
    metaType: App.MetaTypes;
    indexNumber: number;
    sportId: string;
    meta: View.Meta[];
}

const textForMatchPart = ({
    metaType,
    indexNumber,
    sportId,
    meta,
}: TextForMatchPart): string => {
    const isNumerableScoreTypes = checkIsNumerableMetaTypes(metaType, sportId);
    const config = CONFIG_BY_SPORTS[sportId];

    const matchTitle =
        config?.customMetaTypesNaming?.[metaType] ??
        getByKey(MetaTypesTitleMapping, metaType) ??
        '';

    if (!isNumerableScoreTypes) return matchTitle;

    return (
        config?.titleParser?.({
            index: indexNumber,
            text: matchTitle,
            meta,
            type: metaType,
        }) ?? (Number(indexNumber) || 0) + matchTitle.toUpperCase()
    );
};

const currentMetaParser = (
    meta: View.Meta[],
    sportId: string
): View.Meta | undefined => {
    const config = CONFIG_BY_SPORTS[sportId];

    const getMetaPart = config?.getMetaPart;

    if (getMetaPart) return getMetaPart(meta, sportId);

    const sortMap = config?.timeSort;

    if (!sortMap) return last(meta);

    return last(
        meta.sort((a, b) => (sortMap[a.name] || 0) - (sortMap[b.name] || 0))
    );
};

const getCurrentMatchPart = (
    meta: View.Meta[],
    { status, sportId }: View.Match
): MatchPartInfo => {
    const config = CONFIG_BY_SPORTS[sportId];

    let result: MatchPartInfo = {
        sportId,
        status,
        text: '',
        isTimeDisabled: config?.isTimeDisabled,
        indexNumber: 9999,
        type: 'unknown' as any,
    };

    const currMeta = currentMetaParser(meta, sportId);

    if (!currMeta) return result;

    const { value, name } = currMeta;

    const isTimeDisabled =
        config?.isTimeDisabled ??
        config?.customMetaTypesTimeDisabled?.includes(name as App.MetaTypes);

    const customIndexNumber = config?.partStartsFromZero?.includes(
        name as App.MetaTypes
    )
        ? Number(value) + 1
        : Number(value);

    result = {
        sportId,
        status,
        text: textForMatchPart({
            metaType: name as App.MetaTypes,
            indexNumber: customIndexNumber,
            sportId,
            meta,
        }),
        type: name as App.MetaTypes,
        indexNumber: customIndexNumber,
        isTimeDisabled,
    };

    return result;
};

export default getCurrentMatchPart;
